import { useEffect, useMemo, useRef, useState } from 'react'
import PageOverlayController from '../page-overlay-controller'
import 'react-tooltip/dist/react-tooltip.css'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentDocument } from '../../redux/application-slice'
import {
  selectCurrentPage,
  selectDocumentLoaded,
  selectShowCustomLabels,
  selectNewRevision,
  selectShowRedline,
  setShowRedline,
} from '../../redux/viewer-slice'
import ViewerShortcuts from '../viewer/viewer-shortcuts'
import ReactDOM from 'react-dom'
import DocumentViewerSidelingContainer from './document-viewer-sideling-container'
import BlacklinePopover from '../overlay/blackline-popover'
import OverlayContextMenu from '../overlay/overlay-context-menu'
import { OverlayRefClicked } from '../page-overlay/page-overlay'
import DocumentViewerOptionsHeader from './components/document-viewer-header-options'
import DocumentViewerHeaderMisc from './components/document-viewer-header-misc'
import DocumentViewerIcons from './components/document-viewer-icons'
import DocumentViewerContextMenu from './components/document-viewer-context-menu'
import { useDocumentViewer } from '../../hooks/use-document-viewer'
import DocumentViewerViewOptions from './components/document-viewer-view-options'
import { useElementSize } from '@mantine/hooks'
import { DocumentViewerCloseWorkflowSidebar } from './document-viewer-close-workflow-sidebar'
import { DocumentViewerCrossReference } from './components/cross-reference/document-viewer-cross-reference'
import { Tooltip } from 'react-tooltip'
import { Switch } from '@mantine/core'
import { Revision } from '../../shared/interfaces/project/document/revision/revision.interface'
import { useGetDocumentBlacklineQuery } from '../../redux/api-slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetUserProfileQuery } from '../../redux/api-slice'

function DocumentViewer() {
  const scrollView = useRef<HTMLDivElement | null>(null)
  const isDocumentLoaded = useSelector(selectDocumentLoaded)
  const viewer = useRef<HTMLDivElement | null>(null)
  const { data: user } = useGetUserProfileQuery(undefined)
  const {
    sidelingLeftOffset,
    onSelectBlackline,
    selectedBlacklineElement,
    renderedPages,
    lastClickedRef,
  } = useDocumentViewer(viewer, scrollView)
  const [overlaysClicked, setOverlaysClicked] =
    useState<OverlayRefClicked | null>(null)

  const currentPage = useSelector(selectCurrentPage)
  const currentDocument = useSelector(selectCurrentDocument)
  const showCustomLabels = useSelector(selectShowCustomLabels)
  const newRevision = useSelector(selectNewRevision) as Revision | null
  const { ref, width } = useElementSize()
  const dispatch = useDispatch()
  const showRedline = useSelector(selectShowRedline)
  const { currentData: documentBlacklines } = useGetDocumentBlacklineQuery(
    currentDocument?.id ?? skipToken,
    {
      pollingInterval: 3000,
    }
  )

  const webviewer = window?.document?.getElementById('virtualListContainer')

  const onToggleRedline = () => {
    dispatch(setShowRedline(!showRedline))
  }

  const hasBlackline = useMemo(() => {
    if (!currentDocument) {
      return false
    }

    return Boolean(currentDocument?.previous_document_version)
  }, [currentDocument])

  useEffect(() => {
    dispatch(setShowRedline(false))
  }, [currentDocument, dispatch])

  const blacklineFile = useMemo(() => {
    if (!documentBlacklines?.length) {
      return null
    }
    return documentBlacklines.find(
      (blackline) => blackline.blackline_type === 2
    )?.file
  }, [documentBlacklines])

  return (
    <div
      className="flex h-full items-stretch overflow-hidden"
      style={{ maxWidth: '100vw' }}
    >
      <div className={'bg-white-100 flex min-w-0 flex-1 flex-grow flex-col'}>
        <div
          className={
            'min-w-0 flex-shrink-0 justify-between border-b border-gray-300 bg-[#edf0f5]'
          }
          ref={ref}
        >
          <div className="flex w-full items-center">
            <div className="flex w-1/3 min-w-0 items-center justify-start">
              <DocumentViewerViewOptions />
              {hasBlackline ? (
                <div
                  data-tooltip-id={'show-redline-tooltip'}
                  data-tooltip-content={
                    !blacklineFile
                      ? 'Please wait a moment, Provision is preparing the document comparison.'
                      : 'Toggle Redline'
                  }
                >
                  <Switch
                    checked={showRedline}
                    onChange={onToggleRedline}
                    disabled={!blacklineFile}
                    size="xs"
                    label={
                      <div className="text-xs text-gray-500 group-hover:text-black">
                        {width > 750 ? 'Show Redline' : null}
                      </div>
                    }
                  />
                </div>
              ) : null}
            </div>
            <div className="flex w-1/3 min-w-0 items-center justify-center">
              <DocumentViewerOptionsHeader />
            </div>
            <div className="flex w-1/3 min-w-0 items-center justify-end">
              <DocumentViewerHeaderMisc condensed={width < 1000} />
              <DocumentViewerCloseWorkflowSidebar />
            </div>
          </div>
        </div>
        <div
          id="document-viewer"
          className={'flex h-full overflow-auto bg-gray-200'}
          ref={scrollView}
        >
          <div
            className={`webviewer z-10 mx-auto ${!isDocumentLoaded ? 'hidden' : ''}`}
            id="webviewer-wrapper"
            ref={viewer}
          />
          {isDocumentLoaded && user?.feature_flags?.section_reference && (
            <div className="pointer-events-none absolute inset-0">
              <DocumentViewerCrossReference />
            </div>
          )}
        </div>
      </div>
      {/* VIEW/EDIT REVISION SIDELING */}
      {webviewer &&
        ReactDOM.createPortal(
          <>
            <div
              id={'revisions_container'}
              className={'absolute z-[31] h-96'}
              style={{
                top: 0,
                left: sidelingLeftOffset,
              }}
            >
              <DocumentViewerSidelingContainer currentPage={currentPage} />
            </div>
            {showCustomLabels && <DocumentViewerIcons />}
            {/* CONTEXT MENU FOR SELECTED TEXT */}
            <DocumentViewerContextMenu />
          </>,
          webviewer
        )}
      <PageOverlayController
        onSelectBlackline={onSelectBlackline}
        pages={renderedPages}
        setOverlaysClicked={setOverlaysClicked}
        overlaysClicked={overlaysClicked}
      />
      <ViewerShortcuts />
      <BlacklinePopover
        selectedDocument={currentDocument}
        referenceElement={selectedBlacklineElement}
      />
      {overlaysClicked ? (
        <OverlayContextMenu
          overlays={overlaysClicked.overlayRefs}
          referenceElement={lastClickedRef.current}
          setOverlaysClicked={setOverlaysClicked}
        />
      ) : null}
      <Tooltip id={'show-redline-tooltip'} style={{ zIndex: 100 }} />
    </div>
  )
}

export default DocumentViewer
